/**
 * Pairvelle public vocabulary + compatibility mapping to legacy dynamic_role.
 * Internal DB enums are NOT renamed in place; sync at write boundaries.
 */
import type { DynamicRole } from './types'

export type OutreachPreference = 'initiate' | 'be_approached' | 'either'
export type ConnectionIntent = 'long_term' | 'casual' | 'exploring'

export const OUTREACH_LABEL: Record<OutreachPreference, string> = {
  initiate: 'Initiate',
  be_approached: 'Be approached',
  either: 'Either',
}

export const CONNECTION_INTENT_LABEL: Record<ConnectionIntent, string> = {
  long_term: 'Subscribe',
  casual: 'Flirt',
  exploring: 'Meet',
}

export const OUTREACH_OPTIONS: Array<{ value: OutreachPreference; blurb: string }> = [
  { value: 'initiate', blurb: 'I like starting conversations and making the first move.' },
  { value: 'be_approached', blurb: 'I prefer people to reach out first.' },
  { value: 'either', blurb: 'Open either way — chemistry decides.' },
]

export const CONNECTION_INTENT_OPTIONS: Array<{ value: ConnectionIntent; blurb: string }> = [
  { value: 'long_term', blurb: 'Build a closer circle — regulars, supporters, and repeat connection.' },
  { value: 'casual', blurb: 'Playful chemistry and light conversation.' },
  { value: 'exploring', blurb: 'Open to meeting new people and seeing where it goes.' },
]

export const LIFESTYLE_TAG_SUGGESTIONS = [
  'Streaming',
  'Fitness',
  'Fashion',
  'Travel',
  'Gaming',
  'Live music',
  'Night owl',
  'Cosplay',
  'Direct communication',
  'Low drama',
  'Fine dining',
  'Weekend escapes',
] as const

/** Legacy dynamic_role → public outreach preference */
export function outreachFromDynamic(role: DynamicRole): OutreachPreference {
  if (role === 'provider') return 'initiate'
  if (role === 'recipient') return 'be_approached'
  return 'either'
}

/** Public outreach → legacy dynamic_role (for matching engine) */
export function dynamicFromOutreach(outreach: OutreachPreference): DynamicRole {
  if (outreach === 'initiate') return 'provider'
  if (outreach === 'be_approached') return 'recipient'
  return 'flexible'
}

export function defaultShowOutreach(outreach: OutreachPreference): OutreachPreference[] {
  if (outreach === 'initiate') return ['be_approached', 'either']
  if (outreach === 'be_approached') return ['initiate', 'either']
  return ['initiate', 'be_approached', 'either']
}

export function showOutreachFromLegacy(showDynamics: DynamicRole[]): OutreachPreference[] {
  return showDynamics.map(outreachFromDynamic)
}

export function showDynamicsFromOutreach(show: OutreachPreference[]): DynamicRole[] {
  return show.map(dynamicFromOutreach)
}

export function outreachCompatible(
  viewerWants: OutreachPreference[],
  candidate: OutreachPreference,
): boolean {
  const wants = new Set(viewerWants)
  if (wants.size === 0) return false
  if (wants.has(candidate)) return true
  if (candidate === 'either' && (wants.has('initiate') || wants.has('be_approached') || wants.has('either'))) {
    return true
  }
  return false
}

export type AccountStandingSnapshot = {
  standing: 'good' | 'limited' | 'suspended' | 'pending_review'
  visibilityStatus: 'searchable' | 'limited' | 'hidden'
  searchable: boolean
  canMessage: boolean
  canIntroduce: boolean
  verificationStatus: 'none' | 'pending' | 'verified' | 'rejected'
  dailyInterestLimit: number
  dailyInterestsRemaining: number
  activeNotices: Array<{
    id: string
    category: string
    reasonCode: string
    summary: string
    details: string | null
    contentRef: string | null
    startsAt: string
    endsAt: string | null
    status: string
  }>
  appeals: Array<{
    id: string
    noticeId: string | null
    status: string
    createdAt: string
    response: string | null
  }>
  messageStrikeStreak: number
  messageStrikeLimit: number
  messageSuspendedUntil: string | null
}

export type StakeStatus = 'none' | 'held' | 'returned' | 'consumed'
